<script setup>
/**
 * Cloud mode: the account, the org whose ledger is open (either kind), its
 * members, and the passkeys that sign in to it. On a device in local mode, what
 * the cloud gains and costs — said plainly, because it changes who can read it.
 *
 * Switching org, mode or account reloads the app: the database underneath is a
 * different one, and boot is where a database is chosen and opened.
 *
 * Moving the ledger — to or from the cloud, into or out of encryption — is
 * `LedgerStates.vue`'s, above this in the tab; a backup is offered here first.
 * An owner can delete the org, for everyone in it.
 *
 * Settings' Sync tab. Adding a device happens right here too (DevicePanel, in
 * place of the rest), and "a protected backup" goes to the Your data tab.
 */
import { computed, onMounted, reactive, ref, watch } from 'vue'
import DevicePanel from './DevicePanel.vue'
import Icon from '../ui/Icon.vue'
import { state, toast } from '../../stores/state'
import { errorText, t } from '../../stores/i18n'
import * as cloud from '../../stores/cloud'
import { canScan } from '../../camera'
import { setVaultOrg, vaultFile } from '../../stores/sync'
import { dropLater } from '../../db/drops'

const inCloud = computed(() => cloud.getMode() === 'cloud' && !!state.cloud.account)
// In an encrypted org the account arrives after the app is up (it opens offline).
const account = computed({ get: () => state.cloud.account, set: (value) => (state.cloud.account = value) })
/** An encrypted org: its ledger is on this device, not the server's to move or encrypt. */
const sealed = computed(() => state.sync.on)
const org = computed(() => state.cloud.org)
const owner = computed(() => org.value?.role === 'owner')
const people = ref([])
const busy = ref('')
const error = ref('')
const form = reactive({ orgName: '', email: '', role: 'member', passkeyName: '' })

const ROLES = ['owner', 'member', 'viewer']

const emit = defineEmits(['goto'])

/** Showing or scanning a code, drawn in place of the rest until it is done. */
const device = ref(null)

/** The phone scans; any device can show. */
const scanner = canScan()

async function attempt(what, fn) {
  busy.value = what
  error.value = ''
  try {
    return await fn()
  } catch (err) {
    error.value = err?.name === 'NotAllowedError' ? t('cloud.passkeyCancelled') : errorText(err)
  } finally {
    busy.value = ''
  }
}

const loadMembers = () =>
  attempt('members', async () => {
    people.value = await cloud.members(org.value.id)
  })

onMounted(() => {
  if (inCloud.value) loadMembers()
})
watch(inCloud, (yes) => yes && !people.value.length && loadMembers())



/**
 * Deleting the org, for everyone in it — its ledger on the server, or its
 * encrypted history. Every device on it is told it is gone. This device then
 * opens another org, or its own ledger if it is in none.
 */
const deleteOrg = () =>
  attempt('delete', async () => {
    if (!confirm(t('cloud.deleteConfirm', { name: org.value.name }))) return
    // Our own socket is closed as gone too: this page reloads by itself, below.
    state.cloud.encrypting = true
    try {
      await cloud.deleteOrg(org.value.id)
    } catch (err) {
      state.cloud.encrypting = false
      throw err
    }
    if (sealed.value) {
      dropLater(vaultFile(org.value.id))
      dropLater(`files:${org.value.id}`)
    }
    setVaultOrg(null)
    cloud.setOrgId(null)
    location.reload()
  })
const signOut = () =>
  attempt('signout', async () => {
    await cloud.signOut()
    location.reload()
  })

const newOrg = () =>
  attempt('org', async () => {
    const made = await cloud.createOrg(form.orgName.trim())
    cloud.setOrgId(made.id)
    location.reload()
  })

const addPerson = () =>
  attempt('add', async () => {
    await cloud.addMember(org.value.id, form.email.trim(), form.role)
    form.email = ''
    toast(t('cloud.added'), 'good')
    await loadMembers()
  })
const changeRole = (person, role) =>
  attempt('role', async () => {
    people.value = await cloud.setRole(org.value.id, person.userId, role)
  }).then(loadMembers)
const removePerson = (person) =>
  attempt('remove', async () => {
    await cloud.removeMember(org.value.id, person.userId)
    if (person.userId === account.value.user.id) {
      cloud.setOrgId(null)
      location.reload()
      return
    }
    await loadMembers()
  })

const addPasskey = () =>
  attempt('passkey', async () => {
    account.value = await cloud.addPasskey(form.passkeyName.trim())
    form.passkeyName = ''
    toast(t('cloud.passkeyAdded'), 'good')
  })
const dropPasskey = (key) =>
  attempt('passkey', async () => {
    account.value = await cloud.removePasskey(key.id)
  })
</script>

<template>
  <div>
    <div v-if="device" data-device-inline>
      <button class="btn-ghost -ml-2 mb-3 !px-2 !text-[13px]" @click="device = null">
        <Icon name="left" :size="15" /> {{ t('cloud.title') }}
      </button>
      <DevicePanel :payload="device" @done="device = null" />
    </div>

    <!-- This device keeps its own data: the way into cloud mode, and what it costs. -->
    <div v-else-if="!inCloud" class="grid gap-4" data-cloud="local">
      <ul class="grid gap-2 text-[13.5px] leading-relaxed text-muted">
        <li class="flex gap-2"><Icon name="check" :size="15" class="mt-1 shrink-0 text-accent" />{{ t('cloud.gainDevices') }}</li>
        <li class="flex gap-2"><Icon name="check" :size="15" class="mt-1 shrink-0 text-accent" />{{ t('cloud.gainShare') }}</li>
        <li class="flex gap-2"><Icon name="alert" :size="15" class="mt-1 shrink-0 text-warn" />{{ t('cloud.costRead') }}</li>
        <li class="flex gap-2"><Icon name="alert" :size="15" class="mt-1 shrink-0 text-warn" />{{ t('cloud.costOffline') }}</li>
      </ul>
      <!-- A phone joining an account another device already uses. -->
      <div v-if="scanner" class="grid gap-2 border-t border-line-soft pt-4" data-join>
        <p class="text-[12.5px] text-muted">{{ t('cloud.joinByCode') }}</p>
        <button class="btn-soft justify-center" @click="device = { mode: 'scan', join: true }">
          <Icon name="scan" :size="16" /> {{ t('signin.scanCode') }}
        </button>
      </div>
    </div>

    <div v-else class="grid gap-6" data-cloud="signed-in">
      <!-- The org whose ledger is open. -->
      <section class="grid gap-2">
        <p class="text-[12.5px] font-semibold text-muted">{{ t('cloud.org') }}</p>
        <form class="flex gap-2" @submit.prevent="newOrg">
          <input v-model="form.orgName" class="input" :placeholder="t('cloud.newOrgPlaceholder')" :aria-label="t('cloud.newOrg')" />
          <button class="btn-soft shrink-0 whitespace-nowrap" :disabled="!form.orgName.trim() || busy === 'org'">
            {{ t('cloud.newOrg') }}
          </button>
        </form>
      </section>

      <!-- Who shares it. Owners manage; anyone may leave. -->
      <section class="grid gap-2" data-members>
        <p class="text-[12.5px] font-semibold text-muted">{{ t('cloud.members') }}</p>
        <ul class="grid gap-1.5">
          <li v-for="p in people" :key="p.userId" class="flex items-center gap-2 text-[13.5px]" :data-member="p.email">
            <span class="min-w-0 flex-1 truncate">{{ p.email }}</span>
            <select
              v-if="owner"
              class="input !w-auto !py-1.5 !text-[12.5px]"
              :value="p.role"
              :aria-label="t('cloud.roleFor', { email: p.email })"
              @change="changeRole(p, $event.target.value)"
            >
              <option v-for="r in ROLES" :key="r" :value="r">{{ t(`cloud.role.${r}`) }}</option>
            </select>
            <span v-else class="text-[12.5px] text-faint">{{ t(`cloud.role.${p.role}`) }}</span>
            <button
              v-if="owner || p.userId === account.user.id"
              class="btn-ghost !px-1.5 !py-1"
              :aria-label="p.userId === account.user.id ? t('cloud.leave') : t('cloud.removeFor', { email: p.email })"
              @click="removePerson(p)"
            >
              <Icon name="x" :size="15" />
            </button>
          </li>
        </ul>
        <form v-if="owner" class="flex flex-wrap gap-2" @submit.prevent="addPerson">
          <input
            v-model="form.email"
            type="email"
            class="input min-w-0 flex-1"
            :placeholder="t('cloud.emailPlaceholder')"
            :aria-label="t('cloud.addPerson')"
          />
          <select v-model="form.role" class="input !w-auto" :aria-label="t('cloud.newRole')">
            <option v-for="r in ROLES" :key="r" :value="r">{{ t(`cloud.role.${r}`) }}</option>
          </select>
          <button class="btn-soft whitespace-nowrap" :disabled="!form.email.trim() || busy === 'add'">{{ t('cloud.addPerson') }}</button>
        </form>
        <p class="text-[12px] text-faint">{{ t('cloud.rolesHint') }}</p>
      </section>

      <!-- More devices on this account: by QR code, the phone scanning. -->
      <section class="grid gap-2" data-devices>
        <p class="text-[12.5px] font-semibold text-muted">{{ t('cloud.devices') }}</p>
        <p class="text-[12.5px] text-faint">{{ t('cloud.devicesHint') }}</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn-soft" @click="device = { mode: 'show' }">
            <Icon name="qr" :size="16" /> {{ t('signin.showCode') }}
          </button>
          <button v-if="scanner" class="btn-soft" @click="device = { mode: 'scan' }">
            <Icon name="scan" :size="16" /> {{ t('signin.scanCode') }}
          </button>
        </div>
      </section>

      <!-- How this account signs in. -->
      <section class="grid gap-2" data-passkeys>
        <p class="text-[12.5px] font-semibold text-muted">{{ t('cloud.passkeys') }}</p>
        <ul v-if="account.passkeys.length" class="grid gap-1.5">
          <li v-for="k in account.passkeys" :key="k.id" class="flex items-center gap-2 text-[13.5px]">
            <Icon name="check" :size="15" class="shrink-0 text-accent" />
            <span class="min-w-0 flex-1 truncate">{{ k.name }}</span>
            <button class="btn-ghost !px-1.5 !py-1" :aria-label="t('cloud.removePasskey', { name: k.name })" @click="dropPasskey(k)">
              <Icon name="trash" :size="15" />
            </button>
          </li>
        </ul>
        <p v-else class="text-[12.5px] text-faint">{{ t('cloud.noPasskeys') }}</p>
        <form v-if="cloud.passkeysSupported()" class="flex gap-2" @submit.prevent="addPasskey">
          <input v-model="form.passkeyName" class="input" :placeholder="t('cloud.passkeyNamePlaceholder')" :aria-label="t('cloud.passkeyName')" />
          <button class="btn-soft shrink-0 whitespace-nowrap" :disabled="busy === 'passkey'">{{ t('cloud.addPasskey') }}</button>
        </form>
      </section>

      <p v-if="state.cloud.connection === 'reconnecting'" class="text-[12.5px] text-warn">{{ t('cloud.reconnecting') }}</p>

      <!-- Deleting the org: its ledger, for everyone in it. -->
      <section v-if="owner" class="grid gap-2 border-t border-line-soft pt-4" data-delete-org>
        <p class="text-[12.5px] text-muted">{{ t('cloud.deleteLead') }}</p>
        <button class="btn-danger justify-self-start" :disabled="busy === 'delete'" @click="deleteOrg">
          <Icon name="trash" :size="15" /> {{ t('cloud.deleteOrg', { name: org.name }) }}
        </button>
      </section>

      <div class="border-t border-line-soft pt-4">
        <button class="btn-soft" :disabled="busy === 'signout'" @click="signOut">{{ t('cloud.signOut') }}</button>
      </div>
    </div>

    <p v-if="error" class="mt-4 flex items-start gap-2 text-[13px] text-danger" role="alert">
      <Icon name="alert" :size="15" class="mt-0.5 shrink-0" /> {{ error }}
    </p>

  </div>
</template>
